import React, { useState, useEffect } from 'react';
import Input from '../components/common/Input';
import Switch from '../components/common/Switch';
import RadioButton from '../components/common/RadioButton';
import { Link, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  useGetUserProfileQuery,
  useEmailVerifyMutation,
} from '../redux/apiSlice';
import { Loader, Loader2, Share2 } from 'lucide-react';
import moment from 'moment';
import * as Yup from 'yup';
import { resetEmailSchema } from '../validations/SchemaValidations';
import { useCookieConsent } from '../providers/CookieConsentContext';

interface LinkState {
  createdAt?: string;
}
const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { data: userProfile, error, isLoading } = useGetUserProfileQuery();
  const [emailVerify, { isLoading: emailVerifyLoading }] =
    useEmailVerifyMutation();

  const [email, setEmail] = useState('');
  const [createdDate, setJoinedDate] = useState(
    moment(userProfile?.created_at).format('MMMM D, YYYY'),
  );
  const [expireDate, setExpireDate] = useState(
    moment(userProfile?.created_at).add(3, 'days').format('MMMM D, YYYY'),
  );

  const [confirmEmail, setConfirmEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({}); // <-- Updated typing here
  const [notificationOptIn, setNotificationOptIn] = useState(true);
  const [emailFrequency, setEmailFrequency] = useState('quarterly');

  const { toggleSettings } = useCookieConsent();

  useEffect(() => {
    if (userProfile) {
      setEmail('');
      setConfirmEmail('');
    }
  }, [userProfile]);

  const handleSaveChanges = async () => {
    try {
      setErrors({});

      const currentEmail = userProfile?.email || '';

      await resetEmailSchema(currentEmail).validate(
        { email, confirmEmail },
        { abortEarly: false },
      );

      await emailVerify({ newMail: email }).unwrap();
      toast.success('Email verification request sent successfully!');

      setEmail('');
      setConfirmEmail('');
    } catch (validationError) {
      if (validationError instanceof Yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        validationError.inner.forEach((err) => {
          if (err.path) {
            validationErrors[err.path] = err.message;
          }
        });
        setErrors(validationErrors);
      }
    }
  };

  const handleNoOp = () => {
    console.log('test input');
  };

  const handleShareReferralCode = async () => {
    const referralCode = userProfile?.referral_code;
    if (referralCode) {
      try {
        await navigator.clipboard.writeText(referralCode);
        toast.success('Referral code copied to clipboard!');
      } catch (err) {
        toast.error('Failed to copy referral code');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader className="animate-spin" size={48} />
      </div>
    );
  }

  if (error) return <p>Error loading profile data</p>;

  return (
    <div className="flex flex-col text-[14px] font-roboto md:flex-row justify-between p-8 gap-5 bg-gray-50 min-h-screen">
      <div className="bg-white shadow-md rounded-lg p-6 w-full md:w-1/2 mb-6 md:mb-0">
        <h2 className="text-[28px] font-bold mb-4">Profile</h2>
        <div className="mb-4">
          <label className="block text-black mb-2">Joined Date</label>
          <Input
            type="text"
            value={
              userProfile?.created_at
                ? moment(userProfile?.created_at).format('MMMM D, YYYY')
                : 'N/A'
            }
            onChange={handleNoOp}
            readOnly
            className="rounded-lg border-[#505050]"
            disabled={true}
          />
        </div>
        <div className="mb-4">
          <label className="block text-black mb-2">Email</label>
          <Input
            type="email"
            value={userProfile?.email}
            onChange={handleNoOp}
            readOnly
            className="rounded-lg border-[#505050]"
            disabled={true}
          />
        </div>
        <div className="mb-4">
          <label className="block text-black mb-2">Password</label>
          <Input
            type="password"
            value={userProfile?.decrypted_password}
            onChange={handleNoOp}
            readOnly
            className="rounded-lg border-[#505050]"
            disabled={true}
          />
        </div>
        <div className="mb-4">
          <label className="block text-black mb-2">Referral Code</label>
          <div className="relative">
            <Input
              type="text"
              value={userProfile?.referral_code || 'N/A'}
              onChange={handleNoOp}
              readOnly
              className="rounded-lg border-[#505050] pr-10"
              disabled={true}
            />
            {userProfile?.referral_code && (
              <button
                onClick={handleShareReferralCode}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[#023f81] hover:text-[#034a97] transition-colors"
                title="Share referral code"
              >
                <Share2 size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6 w-full md:w-1/2">
        <h2 className="text-[28px] font-bold mb-4">Account Settings</h2>

        <div className="block text-black mb-2">Notification Preferences</div>
        <div className="mb-4 flex items-center">
          <Switch
            checked={notificationOptIn}
            onChange={(e) => setNotificationOptIn(e.target.checked)}
          />
          <span className="text-black">
            Opt in for Promotional and Marketing Material (default)
          </span>
        </div>

        {/* <div className="mb-4">
          <label className="block text-black mb-2">
            Progress Report and Personalized Feedback Email Frequency:
          </label>
          <div className="flex gap-4">
            <RadioButton
              label="Monthly"
              name="emailFrequency"
              value="monthly"
              checked={emailFrequency === 'monthly'}
              onChange={() => setEmailFrequency('monthly')}
              labelSize="text-[14px]"
            />
            <RadioButton
              label="Quarterly (default)"
              name="emailFrequency"
              value="quarterly"
              checked={emailFrequency === 'quarterly'}
              onChange={() => setEmailFrequency('quarterly')}
              labelSize="text-[14px]"
            />
            <RadioButton
              label="Never"
              name="emailFrequency"
              value="never"
              checked={emailFrequency === 'never'}
              onChange={() => setEmailFrequency('never')}
              labelSize="text-[14px]"
            />
          </div>
        </div> */}

        <div className="mb-4">
          <label className="block text-black mt-5">Update Email Address:</label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="rounded-lg border-[#505050]"
          />
          <p
            className={`text-red-500 text-sm ${errors.email ? 'visible' : 'invisible'}`}
          >
            {errors.email || 'Placeholder text'}
          </p>
          <label className="block text-black">Confirm Email Address:</label>
          <Input
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder="Confirm your email"
            className="rounded-lg border-[#505050]"
          />
          <p
            className={`text-red-500 text-sm ${errors.confirmEmail ? 'visible' : 'invisible'}`}
          >
            {errors.confirmEmail || 'Placeholder text'}
          </p>
          <button
            onClick={handleSaveChanges}
            className="bg-[#023f81] text-white flex items-center justify-center px-4 py-2 rounded mt-4 min-w-[122px] h-[36px]" // Added flex, items-center, and justify-center
            disabled={emailVerifyLoading}
          >
            {emailVerifyLoading ? (
              <Loader2 className="animate-spin h-6 w-6" />
            ) : (
              'Save Changes'
            )}
          </button>
        </div>

        <div className="mt-6 text-[#023f81]">
          <button
            onClick={() => {
              navigate('/profile/subscription', {
                state: { createdAt: createdDate, expiredAt: expireDate },
              });
            }}
            className="text-blue-500 hover:underline"
          >
            Subscription Management & Payment Information
          </button>
          <br />
          <button
            onClick={toggleSettings}
            className="text-blue-500 hover:underline"
          >
            Cookie Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
